import { last } from "@de-xima/xima-common-js-lang";
import { byId, toJQuery } from "@de-xima/xima-common-js-dom";
import $ from "jquery";

import { FrameBusChildHandler, getInstance } from "./FramebusHandler.ts";
import { Notification, NotificationHistory } from "./NotificationHistory.ts";
import { findWidgetForElement, getWidgetByVar, runRemoteCommand } from "./Util.ts";
import type { IHideDialogOptions, IShowDialogOptions } from "./Header.ts";

/** Time that needs to pass while loading before the loading dialog is shown. */
const GlobalLoadingDelay = 750;

let GlobalLoadingTimeout: number | undefined;

/**
 * Counter that increments when `onGlobalLoadStart` is called and decrements
 * when `onGlobalLoadEnd` is called.
 */
let onGlobalLoadStartNestingLevel = 0;

/**
 * Clears the loading dialog timeout set by `onGlobalLoadStart`.
 */
function clearGlobalLoadingTimeout(): void {
  if (GlobalLoadingTimeout !== undefined) {
    clearTimeout(GlobalLoadingTimeout);
    GlobalLoadingTimeout = undefined;
  }
}

/**
 * @param notification Growl message to show.
 * @param growlWidgetVar Widget variable of the growl widget component.
 * @returns The newly rendered growl message.
 */
function renderGrowlMessage(notification: PrimeFaces.FacesMessage, growlWidgetVar: string): JQuery {
  const growl = getWidgetByVar(growlWidgetVar, PrimeFaces.widget.Growl);
  if (growl === undefined) {
    console.warn("Cannot update client notification, growl widget not found", growlWidgetVar);
    return $();
  }
  growl.add(notification);
  return growl.jq.find(".ui-growl-item-container").last().find(".ui-growl-item");
}

/**
 * Checks whether the source component of the AJAX request has defined a custom
 * delay via `data-loading-delay`. If not, returns the default timeout.
 *
 * For example, to set the delay to 500, use:
 *
 * ```xml
 * <p:commandButton listener="..." a:data-loading-delay="500" />
 * ```
 *
 * As a shortcut for always showing the loading dialog immediately (i.e.
 * delay = 0), add the class `loadAction`.
 *
 * ```xml
 * <p:commandButton listener="..." styleClass="loadAction" />
 * ```
 *
 * @returns Loading delay requested for the loading dialog.
 */
function getLoadingDelay() {
  const req = last(PrimeFaces.ajax.Queue.requests);
  if (req === undefined || req.source === undefined) {
    return GlobalLoadingDelay;
  }
  const source = toJQuery(typeof req.source === "string" ? byId(req.source) : req.source);

  if (source.hasClass("load-action")) {
    return 0;
  }

  const customDelay = source.data("loadingDelay") as unknown;
  if (typeof customDelay === "number") {
    return customDelay;
  }
  if (typeof customDelay === "string") {
    return Number.parseInt(customDelay, 10);
  }
  return GlobalLoadingDelay;
}

/**
 * Makes a tooltip stay while the user hovers over it with the mouse.
 * To enable this behavior, set the class `ui-tooltip-stay-on-hover` on a
 * `primefaces:tooltip`.
 *
 * Also, you should set a `hideDelay` on the tooltip, so that the user gets a
 * chance to hover over the tooltip in the first place.
 */
export function keepTooltipMessageOnHover(): void {
  $(document)
    .on(
      "mouseenter",
      ".ui-tooltip.ui-tooltip-stay-on-hover",
      function (this: HTMLElement, _event: JQuery.TriggeredEvent) {
        const widget = findWidgetForElement(this, PrimeFaces.widget.Tooltip);
        if (widget !== undefined) {
          // biome-ignore lint/complexity/useLiteralKeys: We need to access a private member
          widget["clearTimeout"]();
        } else {
          console.warn("Cannot find tooltip widget for", this);
        }
      },
    )
    .on(
      "mouseleave",
      ".ui-tooltip.ui-tooltip-stay-on-hover",
      function (this: HTMLElement, _event: JQuery.TriggeredEvent) {
        const widget = findWidgetForElement(this, PrimeFaces.widget.Tooltip);
        if (widget !== undefined) {
          widget.hide();
        } else {
          console.warn("Cannot find tooltip widget for", this);
        }
      },
    );
}

/**
 * As long as the user hover over the growl message area, do not fade them aways.
 */
export function keepGrowlMessagesOnHover(): void {
  $(document)
    .on("mouseenter", ".ui-growl", function (this: HTMLElement, _event: JQuery.TriggeredEvent) {
      for (const msg of $(this).find(".ui-growl-item-container")) {
        const timeoutId = $(msg).data("timeout") as unknown;
        if (typeof timeoutId === "number") {
          clearTimeout(timeoutId);
        }
      }
    })
    .on("mouseleave", ".ui-growl", function (this: HTMLElement, event: JQuery.TriggeredEvent) {
      const target = event.target instanceof HTMLElement ? event.target : undefined;
      const growl = findWidgetForElement(target, PrimeFaces.widget.Growl);
      if (growl === undefined) {
        console.warn("Did not find the growl widget for", event);
        return;
      }
      if (growl.cfg.sticky === undefined || !growl.cfg.sticky) {
        for (const msg of $(this).find(".ui-growl-item-container")) {
          // biome-ignore lint/complexity/useLiteralKeys: We need to access a private member
          growl["setRemovalTimeout"]($(msg));
        }
      }
    });
}

/**
 * Called when a global AJAX loading event starts
 * (`<p:ajaxStatus onstart="..."/>`)
 * @param dialogWidgetVar Widget variable for the loading dialog.
 */
export function onGlobalLoadStart(dialogWidgetVar: string = "loadingDialog"): void {
  // Send the message to the parent frame which can handle it
  const framebusHandler = getInstance();
  if (framebusHandler instanceof FrameBusChildHandler) {
    framebusHandler.emitGlobalLoadStart(dialogWidgetVar);
  } else {
    onGlobalLoadStartNestingLevel += 1;
    clearGlobalLoadingTimeout();
    const widget = getWidgetByVar(dialogWidgetVar, PrimeFaces.widget.Dialog);
    if (widget === undefined) {
      console.warn("No loading dialog found for widget var:", dialogWidgetVar);
    }
    // Do not open dialog when it is already open
    if (widget !== undefined && !widget.isVisible()) {
      const loadingDelay = getLoadingDelay();
      if (loadingDelay <= 0) {
        // biome-ignore lint/complexity/useLiteralKeys: We need to access a private member
        widget["initPosition"]();
        widget.show();
        GlobalLoadingTimeout = -1;
      } else {
        GlobalLoadingTimeout = window.setTimeout(() => {
          // biome-ignore lint/complexity/useLiteralKeys: We need to access a private member
          widget["initPosition"]();
          widget.show();
        }, loadingDelay);
      }
    }
  }
}

/**
 * Called when a global AJAX loading event results in an error
 * (`<p:ajaxStatus onerror="..." />`). Show a notification to the user.
 * @param statusText Status text of the XHR request.
 * @param summary Localized message to show as the summary of the message
 * informing the user about the error.
 * @param detail Localized message to show as the detail of the message
 * informing the user about the error.
 */
export function onGlobalLoadError(statusText: string, summary: string, detail: string): void {
  // Send the message to the parent frame which can handle it
  const framebusHandler = getInstance();
  if (framebusHandler instanceof FrameBusChildHandler) {
    framebusHandler.emitGlobalLoadError(statusText, summary, detail);
  } else {
    if (statusText !== "abort" && statusText !== "canceled") {
      showNotification(
        {
          detail: detail,
          severity: "error",
          summary: summary,
        },
        undefined,
        false,
      );
    }
  }
}

/**
 * Called when a global AJAX loading event ends (`p:ajaxStatus oncomplete=...`)
 * @param dialogWidgetVar Widget variable for the loading dialog.
 */
export function onGlobalLoadEnd(dialogWidgetVar: string = "loadingDialog"): void {
  // Send the message to the parent frame which can handle it
  const framebusHandler = getInstance();
  if (framebusHandler instanceof FrameBusChildHandler) {
    framebusHandler.emitGlobalLoadEnd(dialogWidgetVar);
  } else {
    clearGlobalLoadingTimeout();
    onGlobalLoadStartNestingLevel -= 1;
    if (onGlobalLoadStartNestingLevel === 0) {
      const dialog = getWidgetByVar(dialogWidgetVar, PrimeFaces.widget.Dialog);
      if (dialog !== undefined) {
        dialog.hide();
      } else {
        console.warn("no loading dialog found for widget var:", dialogWidgetVar);
      }
    }
    if (onGlobalLoadStartNestingLevel < 0) {
      onGlobalLoadStartNestingLevel = 0;
    }
  }
}

/**
 * Called after AJAX requests that may have added new faces messages. Extracts
 * messages and renders them in the history.
 * @param messages List of messages that were added.
 */
export function updateNotificationHistory(messages: PrimeFaces.FacesMessage[] = []): void {
  const notificationHistory = NotificationHistory.getInstance();
  for (const message of messages) {
    const notification = Notification.forGrowlMessage(message);
    notificationHistory.addNotification(notification);
  }
  if (messages.length > 0) {
    const $trigger = $(".fc-nh-trigger");
    $trigger.addClass("fc-nh-new");
    const $icon = $trigger.find(".ico-fc-comment-outline");
    $icon.removeClass("ico-fc-comment-outline");
    $icon.addClass("ico-fc-comment-text-outline");
  }
}

export function removeNotificationIndication(): void {
  const $trigger = $(".fc-nh-trigger");
  $trigger.removeClass("fc-nh-new");
  const $icon = $trigger.find(".ico-fc-comment-text-outline");
  $icon.removeClass("ico-fc-comment-text-outline");
  $icon.addClass("ico-fc-comment-outline");
}

/**
 * Displays the given notification in the browser (via p:growl).
 * @param message Notification to display.
 * @param growlWidgetVar Widget variable for the p:growl component.
 * @param saveInSession If `true`, saves the newly added message in the current
 * session, so that the user can view it even when the reload the page.
 */
export function showNotification(
  message: PrimeFaces.FacesMessage,
  growlWidgetVar: string = "mainGrowl",
  saveInSession: boolean = true,
): void {
  // Check if we are in an iframe inside a backend page without a growl ourself
  // Send the message to the parent frame which can show the growl
  const framebusHandler = getInstance();
  if (framebusHandler instanceof FrameBusChildHandler) {
    framebusHandler.emitShowMessage(message.severity, message.summary, message.detail, saveInSession);
  } else {
    const growlMessage = renderGrowlMessage(message, growlWidgetVar);
    if (growlMessage.length === 0) {
      return;
    }
    updateNotificationHistory([message]);
    // This message was added by us. We set this so we do not add this message again later.
    growlMessage.data("addedByClient", "true");
    if (saveInSession) {
      runRemoteCommand(BackendCommand?.saveSessionMessage, [
        { name: "summary", value: message.summary ?? "" },
        { name: "detail", value: message.detail ?? "" },
        { name: "severity", value: message.severity ?? "info" },
      ]).catch((e) => console.error("Could not save notification in backend", e));
    }
  }
}

/**
 * Shows or hides the loading dialog.
 * @param visible `true` to show the loading dialog, `false` to hide it.
 */
export function loadingDialog(visible: boolean): void {
  // Check if we are in an iframe inside a backend page without a growl ourself
  // Send the message to the parent frame which can show the growl
  const framebusHandler = getInstance();
  if (framebusHandler instanceof FrameBusChildHandler) {
    framebusHandler.emitLoadingDialog(visible ? "show" : "hide");
  } else {
    const widget = getWidgetByVar("loadingDialog", PrimeFaces.widget.Dialog);
    if (visible) {
      widget?.show();
    } else {
      widget?.hide();
    }
  }
}

/**
 * Shows the dialog prompting the user to create an account.
 * @param visible `true` to show the dialog, `false` to hide it.
 */
export function annoyingRegisterPromptDialog(visible: boolean): void {
  if (visible) {
    showDialog("annoyingRegisterPromptDialog", {
      bounceIfVisible: true,
      ignoreMissing: true,
      moveToTop: true,
    });
  } else {
    hideDialog("annoyingRegisterPromptDialog", { ignoreMissing: true });
  }
}

/**
 * Shows the dialog with the given widget var.
 * @param widgetVar Widget var of a PrimeFaces dialog component.
 * @param options Additional options for how the dialog is shown.
 */
export function hideDialog(widgetVar: string, options?: IHideDialogOptions): void {
  // Check if we are in an iframe inside a backend page without a dialog ourself
  const dialog = getWidgetByVar(widgetVar, PrimeFaces.widget.Dialog);
  const framebusHandler = getInstance();
  if (framebusHandler instanceof FrameBusChildHandler && dialog === undefined) {
    framebusHandler.emitHideDialog(widgetVar, options ?? {});
    return;
  }

  // We are at the top page, or the iframe defines the dialog
  if (dialog === undefined) {
    if (!(options?.ignoreMissing ?? false)) {
      console.warn(`Dialog with widget var ${widgetVar} was not found`);
    }
    return;
  }
  dialog.hide();
}

/**
 * Shows the dialog with the given widget var.
 * @param widgetVar Widget var of a PrimeFaces dialog component.
 * @param options Additional options for how the dialog is shown.
 */
export function showDialog(widgetVar: string, options?: IShowDialogOptions): void {
  // Check if we are in an iframe inside a backend page without a dialog ourself
  const dialog = getWidgetByVar(widgetVar, PrimeFaces.widget.Dialog);
  const framebusHandler = getInstance();
  if (framebusHandler instanceof FrameBusChildHandler && dialog === undefined) {
    framebusHandler.emitShowDialog(widgetVar, options ?? {});
    return;
  }

  // We are at the top page, or the iframe defines the dialog
  if (dialog === undefined) {
    if (!(options?.ignoreMissing ?? false)) {
      console.warn(`Dialog with widget var ${widgetVar} was not found`);
    }
    return;
  }
  if (dialog.isVisible()) {
    if (options?.bounceIfVisible ?? false) {
      dialog.jq.effect("bounce");
    }
  } else {
    dialog.show();
  }
  if (options?.moveToTop ?? false) {
    dialog.moveToTop();
  }
}
